import { EntriesQueries, EntrySkeletonType } from 'contentful'
import { z } from 'zod'
import {
  CancellationPoliciesSchema,
  ContentfulAviosOnlyPromotionContent,
  ContentfulAviosOnlyPromotionContentSchema,
  ContentfulCancellationPoliciesRequestParams,
  ContentfulEducationalComponentSchema,
  ContentfulEntriesRequestParams,
  ContentfulEntryRequestParams,
  ContentfulMessageCluster,
  ContentfulMessageClusterRequestParams,
  ContentfulMessageClusterSchema,
  ContentfulStaticPageContentSchema,
  ContentfulStickyBannerSchema,
  ContentfulPromotionBannerSchema,
} from './schemas'
import {
  AviosOnlyPromotionEntry,
  CancellationPoliciesEntry,
  ComponentStickyBannerEntry,
} from './generated/content-models.types'
import createContentfulClient from './contentful.client'
import {
  getContentfulRequestModifiers,
  getSingleItemFromCollection,
  mapEntryFieldsFromContentfulRestResponse,
  mapEntryFieldsFromContentfulRestResponseAsync,
} from './contentful.utils'
import { ContentfulException } from './ContentfulException'

type ContentfulTypeFetchOptions<T extends z.ZodTypeAny> = {
  /** Contentful content type to query for */
  contentType: string
  /** schema to validate the output of the fetch post transform */
  schema: T
  /** When false (default), will validate for exactly one item and throws if 0 or >1 found
   *  When true, allows there to be no items found. */
  allowEmpty?: boolean
}

const fetchContentByIdentifier = async <T extends EntrySkeletonType, S extends z.ZodTypeAny>(
  { contentType, schema, allowEmpty = false }: ContentfulTypeFetchOptions<S>,
  { opCo, identifier, locale, market, preview }: ContentfulEntryRequestParams
) => {
  try {
    const client = createContentfulClient({ preview })

    const collection = await client.getEntries<T>({
      content_type: contentType,
      'fields.identifier': identifier,
      limit: 1,
      include: 10,
      ...getContentfulRequestModifiers({ opCo, market, locale }),
    } as unknown as EntriesQueries<T, undefined>)

    if (allowEmpty && collection.total === 0) {
      return {
        identifier,
        contentType,
      } as z.infer<S>
    }

    const item = getSingleItemFromCollection(collection)

    return await mapEntryFieldsFromContentfulRestResponseAsync(item, schema)
  } catch (error) {
    throw new ContentfulException({
      developerMessage: `Failed to fetch content type for ${contentType}, identifier: ${identifier}, opCo: ${opCo}, locale: ${locale}`,
      originalError: error instanceof Error ? error : undefined,
    })
  }
}

// using a create function here to allow for TS schema argument to be inferred when possible
// but requiring the EntrySkeletonType to be inputted to give a return value
export const createSingleContentFetcher =
  <S extends z.ZodTypeAny>(options: ContentfulTypeFetchOptions<S>) =>
  async <T extends EntrySkeletonType>(requestParams: ContentfulEntryRequestParams) =>
    fetchContentByIdentifier<T, S>(options, requestParams)

export const fetchStaticPageContent = createSingleContentFetcher({
  contentType: 'staticPage',
  schema: ContentfulStaticPageContentSchema,
  allowEmpty: true,
})

export const fetchEducationalComponent = createSingleContentFetcher({
  contentType: 'componentEducational',
  schema: ContentfulEducationalComponentSchema,
  allowEmpty: true,
})

export const fetchMessageClusters = async ({
  clusters,
  locale,
  preview,
}: ContentfulMessageClusterRequestParams): Promise<ContentfulMessageCluster[]> => {
  try {
    const client = createContentfulClient({ preview })
    const collection = await client.getEntries({
      content_type: 'messageCluster',
      'fields.key[in]': clusters,
      limit: 10,
      locale,
    })

    return mapEntryFieldsFromContentfulRestResponse(
      collection.items,
      ContentfulMessageClusterSchema.array()
    )
  } catch (error) {
    throw new ContentfulException({
      developerMessage: `Failed to fetch messageCluster entries for keys: ${clusters.join(', ')}`,
      originalError: error instanceof Error ? error : undefined,
    })
  }
}

export const fetchStickyBanners = async ({
  opCo,
  locale,
  preview,
}: ContentfulEntriesRequestParams) => {
  try {
    const client = createContentfulClient({ preview })

    const collection = await client.getEntries<ComponentStickyBannerEntry>({
      content_type: 'componentStickyBanner',
      ...getContentfulRequestModifiers({ opCo, locale, application: 'reward_platform' }),
    })

    return await mapEntryFieldsFromContentfulRestResponseAsync(
      collection.items,
      ContentfulStickyBannerSchema.array()
    )
  } catch (error) {
    throw new ContentfulException({
      developerMessage: `Failed to fetch componentStickyBanner entries for opCo: ${opCo}, locale: ${locale}`,
      originalError: error instanceof Error ? error : undefined,
    })
  }
}

export const fetchAviosOnlyPromotion = async ({
  opCo,
  locale,
  preview,
}: ContentfulEntriesRequestParams): Promise<ContentfulAviosOnlyPromotionContent> => {
  try {
    const client = createContentfulClient({ preview })
    const collection = await client.getEntries<AviosOnlyPromotionEntry>({
      content_type: 'aviosOnlyPromotion',
      ...getContentfulRequestModifiers({ opCo, locale }),
    })

    const item = getSingleItemFromCollection(collection)
    return await mapEntryFieldsFromContentfulRestResponseAsync(
      item,
      ContentfulAviosOnlyPromotionContentSchema
    )
  } catch (error) {
    throw new ContentfulException({
      developerMessage: `Failed to fetch aviosOnlyPromotion entries for opCo: ${opCo}, locale: ${locale}`,
      originalError: error instanceof Error ? error : undefined,
    })
  }
}

export const fetchPromotionBanners = async ({
  opCo,
  locale,
  preview,
}: ContentfulEntriesRequestParams) => {
  try {
    const client = createContentfulClient({ preview })

    const collection = await client.getEntries({
      content_type: 'componentPromotionBanner',
      ...getContentfulRequestModifiers({ opCo, locale, application: 'reward_platform' }),
    })

    const mappedEntries = mapEntryFieldsFromContentfulRestResponse(collection.items)

    if (!Array.isArray(mappedEntries)) {
      return []
    }

    return mappedEntries.flatMap((entry) => {
      const parsed = ContentfulPromotionBannerSchema.safeParse(entry)
      return parsed.success ? [parsed.data] : []
    })
  } catch (error) {
    throw new ContentfulException({
      developerMessage: `Failed to fetch componentPromotionBanner entries for opCo: ${opCo}, locale: ${locale}`,
      originalError: error instanceof Error ? error : undefined,
    })
  }
}

export const fetchCancellationPolicies = async ({
  locale,
  preview,
}: ContentfulCancellationPoliciesRequestParams) => {
  try {
    const defaultKey = 'default'
    const client = createContentfulClient({ preview })

    const collection = await client.getEntries<CancellationPoliciesEntry>({
      content_type: 'cancellationPolicies',
      'fields.key': defaultKey,
      locale,
    })

    const item = getSingleItemFromCollection(collection)
    return mapEntryFieldsFromContentfulRestResponse(item, CancellationPoliciesSchema)
  } catch (error) {
    throw new ContentfulException({
      developerMessage: `Failed to fetch cancellationPolicies for key: "default", locale: ${locale}`,
      originalError: error instanceof Error ? error : undefined,
    })
  }
}
